import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Activity,
    AlertTriangle,
    ClipboardList,
    Inbox,
    Workflow,
    RotateCcw,
    Stethoscope,
    LogOut,
    Crosshair,
    Layers,
    Megaphone,
    Users,
    CheckCircle2,
    Send,
    FileText,
    Settings as SettingsIcon,
    ChevronRight,
} from "lucide-react";
import { seedDemo } from "@/lib/api";
import { listVerticalTrackers } from "@/lib/chiroApi";
import { useAuth } from "@/lib/auth";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_e5fbfad1-91cd-48c5-b4b1-e04fdc4a0794/artifacts/hl0vo1yr_PROOF%20logo%20and%20concept%20round.png";

// Primary v2 nav. Items flagged comingLater render with a muted style + pill.
const PRIMARY_NAV = [
    { to: "/dashboard", label: "Dashboard", icon: Activity, testid: "nav-dashboard" },
    { to: "/master-campaign", label: "Master Campaign", icon: Crosshair, testid: "nav-master-campaign" },
    // Vertical Trackers is a group with children rendered inline (see below).
    { to: "/campaigns", label: "Campaigns", icon: Megaphone, testid: "nav-campaigns" },
    { to: "/prospects", label: "Prospects", icon: Users, testid: "nav-prospects" },
    {
        to: "/approvals",
        label: "Approvals",
        icon: CheckCircle2,
        testid: "nav-approvals",
    },
    {
        to: "/outreach-queue",
        label: "Outreach Queue",
        icon: Send,
        testid: "nav-outreach-queue",
    },
    { to: "/receipts", label: "Receipts", icon: FileText, testid: "nav-receipts" },
    { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

// Legacy / observability rail kept fully functional; rendered after primary nav.
const LEGACY_NAV = [
    { to: "/log", label: "Action Log", icon: ClipboardList, testid: "nav-log" },
    { to: "/failures", label: "Failures", icon: AlertTriangle, testid: "nav-failures" },
    { to: "/review", label: "Review", icon: Inbox, testid: "nav-review" },
    { to: "/workflows", label: "Workflows", icon: Workflow, testid: "nav-workflows" },
    { to: "/chirotracker", label: "ChiroTracker (legacy)", icon: Stethoscope, testid: "nav-chirotracker" },
];

export default function Layout() {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [trackers, setTrackers] = useState([]);

    useEffect(() => {
        listVerticalTrackers()
            .then(setTrackers)
            .catch(() => setTrackers([]));
    }, []);

    const handleReseed = async () => {
        try {
            await seedDemo();
            toast.success("Demo data reseeded");
            navigate(0);
        } catch (e) {
            toast.error("Reseed failed");
        }
    };

    const handleLogout = async () => {
        await logout();
        toast.success("Signed out");
        navigate("/login", { replace: true });
    };

    return (
        <div className="min-h-screen flex bg-[#090a0b] text-[#f3f4f6]">
            {/* Sidebar */}
            <aside
                className="w-72 shrink-0 border-r border-[#262a2e] bg-[#090a0b] flex flex-col sticky top-0 h-screen overflow-y-auto"
                data-testid="sidebar"
            >
                <div className="p-6 border-b border-[#262a2e] flex items-center gap-3">
                    <img
                        src={LOGO_URL}
                        alt="ProofCommand"
                        className="w-10 h-10 object-contain"
                        data-testid="sidebar-logo"
                    />
                    <div className="leading-tight">
                        <div className="font-sans font-bold tracking-tight text-[15px]">
                            ProofCommand
                        </div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#9ca3af]">
                            No claims. Receipts.
                        </div>
                    </div>
                </div>

                <nav className="flex-1 py-4">
                    {/* Primary v2 nav */}
                    {PRIMARY_NAV.map((n) =>
                        n.to === "/campaigns" ? (
                            <React.Fragment key="vertical-trackers-group">
                                <VerticalTrackersGroup trackers={trackers} />
                                <NavItem n={n} />
                            </React.Fragment>
                        ) : (
                            <NavItem key={n.to} n={n} />
                        ),
                    )}

                    {/* Divider + legacy/observability rail */}
                    <div
                        className="mt-4 mb-2 mx-6 border-t border-[#262a2e]"
                        data-testid="sidebar-legacy-divider"
                    />
                    <div className="px-6 mb-2 font-mono text-[9px] uppercase tracking-[0.25em] text-[#6b7280]">
                        // observability
                    </div>
                    {LEGACY_NAV.map((n) => (
                        <NavItem key={n.to} n={n} />
                    ))}
                </nav>

                <div className="p-4 border-t border-[#262a2e]">
                    <button
                        onClick={handleReseed}
                        data-testid="sidebar-reseed-btn"
                        className="w-full flex items-center justify-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase border border-[#262a2e] hover:border-[#7cc93c] hover:text-[#7cc93c] py-2 rounded-[2px] transition-colors"
                    >
                        <RotateCcw className="w-3 h-3" />
                        Reseed demo
                    </button>
                    <button
                        onClick={handleLogout}
                        data-testid="sidebar-logout-btn"
                        className="mt-2 w-full flex items-center justify-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase border border-[#262a2e] hover:border-[#ff453a] hover:text-[#ff453a] py-2 rounded-[2px] transition-colors"
                    >
                        <LogOut className="w-3 h-3" />
                        Sign out
                    </button>
                    <div className="mt-3 font-mono text-[9px] uppercase tracking-[0.2em] text-[#9ca3af] text-center">
                        v0.2 · proofcommand v2
                    </div>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 relative pc-grid-bg overflow-x-hidden">
                <TopBar />
                <div className="p-8">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}

function NavItem({ n }) {
    const Icon = n.icon;
    return (
        <NavLink
            to={n.to}
            data-testid={n.testid}
            className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 font-mono text-[12px] tracking-[0.15em] uppercase border-l-2 transition-colors ${
                    isActive
                        ? "border-[#7cc93c] text-[#f3f4f6] bg-[#111315]"
                        : "border-transparent text-[#9ca3af] hover:text-[#f3f4f6] hover:bg-[#0f1113]"
                } ${n.comingLater ? "opacity-70" : ""}`
            }
        >
            <Icon className="w-3.5 h-3.5" />
            <span className="flex-1">{n.label}</span>
            {n.comingLater && (
                <span className="text-[8px] tracking-[0.2em] text-[#6b7280] border border-[#262a2e] px-1 py-[1px] rounded-[2px]">
                    SOON
                </span>
            )}
        </NavLink>
    );
}

function VerticalTrackersGroup({ trackers }) {
    return (
        <div data-testid="nav-vertical-trackers-group">
            <NavLink
                to="/vertical-trackers"
                data-testid="nav-vertical-trackers"
                className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-2.5 font-mono text-[12px] tracking-[0.15em] uppercase border-l-2 transition-colors ${
                        isActive
                            ? "border-[#7cc93c] text-[#f3f4f6] bg-[#111315]"
                            : "border-transparent text-[#9ca3af] hover:text-[#f3f4f6] hover:bg-[#0f1113]"
                    }`
                }
            >
                <Layers className="w-3.5 h-3.5" />
                <span className="flex-1">Vertical Trackers</span>
                <ChevronRight className="w-3 h-3" />
            </NavLink>
            <div className="ml-6 border-l border-[#1f2225]">
                {trackers.length === 0 && (
                    <div className="px-4 py-2 font-mono text-[10px] text-[#6b7280]">…</div>
                )}
                {trackers.map((t) => (
                    <NavLink
                        key={t.id}
                        to={`/vertical-trackers/${t.id}`}
                        data-testid={`nav-tracker-${t.slug}`}
                        className={({ isActive }) =>
                            `flex items-center gap-2 pl-4 pr-3 py-1.5 font-mono text-[11px] tracking-[0.1em] transition-colors ${
                                isActive
                                    ? "text-[#7cc93c]"
                                    : "text-[#9ca3af] hover:text-[#f3f4f6]"
                            } ${t.status === "coming_later" ? "opacity-60" : ""}`
                        }
                    >
                        <span className="flex-1 truncate">{t.name}</span>
                        {t.status === "coming_later" && (
                            <span className="text-[8px] tracking-[0.15em] text-[#6b7280]">
                                SOON
                            </span>
                        )}
                        {t.status === "live" && (
                            <span className="w-1.5 h-1.5 rounded-full bg-[#7cc93c]" />
                        )}
                    </NavLink>
                ))}
            </div>
        </div>
    );
}

function TopBar() {
    return (
        <div
            className="sticky top-0 z-20 h-14 px-8 border-b border-[#262a2e] bg-[#090a0b]/85 backdrop-blur-md flex items-center justify-between"
            data-testid="topbar"
        >
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af]">
                mission control · verified execution
            </div>
            <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af]">
                <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#7cc93c] pc-dot-pulse" />
                    system online
                </span>
                <span className="text-[#9ca3af]">{new Date().toISOString().slice(0, 19).replace("T", " ")}Z</span>
            </div>
        </div>
    );
}
